// hooks/useResponsive.ts
"use client";

import { BaseTheme } from "@/themes/custom.theme.config";
import { useEffect, useState } from "react";

const screens = (BaseTheme.screens || {}) as Record<string, string>;

function toNumber(px: string) {
  return parseInt(px.replace("px", ""), 10);
}

type Breakpoint = keyof typeof screens;

type ScreenFlags<T extends string> = {
  [K in T as `is${Capitalize<K>}`]: boolean;
} & {
  [K in T as `only${Capitalize<K>}`]: boolean;
};

type ResponsiveResult = { breakpoint: Breakpoint | null } & ScreenFlags<
  keyof typeof screens
>;

function getBreakpointFromWidth(width: number): Breakpoint | null {
  if (!screens) return null;
  let active: Breakpoint | null = null;
  for (const [key, value] of Object.entries(screens)) {
    if (width >= toNumber(value)) active = key as Breakpoint;
  }
  return active;
}

export function useResponsive(userAgentWidth = 1024): ResponsiveResult {
  // On server → guess from userAgentWidth (default 1024px = desktop)
  const [breakpoint, setBreakpoint] = useState<Breakpoint | null>(
    typeof window === "undefined"
      ? getBreakpointFromWidth(userAgentWidth)
      : null
  );

  useEffect(() => {
    if (!screens) return;

    const queries = Object.entries(screens).map(([key, value]) => {
      return [key, window.matchMedia(`(min-width: ${value})`)] as const;
    });

    function update() {
      let active: Breakpoint | null = null;
      for (const [key, media] of queries) {
        if (media.matches) active = key as Breakpoint;
      }
      setBreakpoint(active);
    }

    update();
    queries.forEach(([_, media]) => media.addEventListener("change", update));

    return () => {
      queries.forEach(([_, media]) =>
        media.removeEventListener("change", update)
      );
    };
  }, []);

  const width =
    typeof window !== "undefined" ? window.innerWidth : userAgentWidth;

  const flags = Object.keys(screens || {}).reduce((acc, key, idx, arr) => {
    const min = toNumber(screens[key]);
    const next = arr[idx + 1] ? toNumber(screens[arr[idx + 1]]) : Infinity;

    const propIs = "is" + key.charAt(0).toUpperCase() + key.slice(1);
    const propOnly = "only" + key.charAt(0).toUpperCase() + key.slice(1);

    (acc as any)[propIs] = width >= min;
    (acc as any)[propOnly] = width >= min && width < next;

    return acc;
  }, {} as ScreenFlags<keyof typeof screens>);

  return {
    breakpoint,
    ...flags,
  };
}
